import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import ConsentCheckbox from "@/components/ConsentCheckbox";
import { Disclaimer } from "@/components/Shared";
import { WizardProgress, FieldGrid, WizardNav, validateStep, PortalHero, ComplianceNote } from "@/components/pathways/FormBits";
import { GraduationCap, Briefcase, Trophy, Heart, Plane, Users, MapPin, HelpCircle, ArrowRight, Sparkles, CheckCircle2, Wrench } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const GOALS = [
  { key: "study", icon: GraduationCap, label: "Study in Australia or New Zealand", desc: "Courses, universities and student visa guidance." },
  { key: "work", icon: Briefcase, label: "Work in Australia or New Zealand", desc: "Skilled and employer-sponsored work options." },
  { key: "pr", icon: Trophy, label: "Explore permanent residency pathways", desc: "Points-tested and sponsored PR options." },
  { key: "family", icon: Heart, label: "Join my partner or family", desc: "Partner and family visa options." },
  { key: "visitor", icon: Plane, label: "Visit Australia or New Zealand", desc: "Tourist and family-sponsored visitor visas." },
  { key: "sponsor", icon: Users, label: "Sponsor a family member", desc: "Bring your parents, partner or children." },
  { key: "onshore", icon: MapPin, label: "I'm already in Australia or NZ and need my next pathway", desc: "Plan your next visa before your current one expires." },
  { key: "unsure", icon: HelpCircle, label: "I'm not sure and need guidance", desc: "A broad assessment to point you the right way." },
];

const TOOLS = {
  study: [
    { label: "GS & SOP Statement Builder", to: "/visa-statement-builder" },
    { label: "Student Funds Calculator", to: "/student-funds-calculator" },
  ],
  work: [{ label: "Skilled Migration Pathway Assessment", to: "/skilled-migration-assessment" }],
  pr: [
    { label: "PR Assessment", to: "/pr-assessment" },
    { label: "Points Calculator", to: "/points-calculator" },
  ],
  family: [{ label: "Partner Visa Assessment", to: "/partner-visa" }],
  sponsor: [{ label: "Parent Visa Pathway Assessment", to: "/parent-visa-finder" }],
  onshore: [{ label: "Temporary Graduate 485 Assessment", to: "/temporary-graduate-485" }],
};

const YN = ["Yes", "No", "Not sure"];
const QUALS = ["Year 12 / High School", "Certificate / Diploma", "Trade qualification", "Bachelor's degree", "Master's degree", "PhD"];
const ENGLISH = ["Basic", "Competent (IELTS 6 / equivalent)", "Proficient (IELTS 7 / equivalent)", "Superior (IELTS 8+ / equivalent)", "Not tested yet"];

const BRANCH_STEPS = {
  study: [
    { title: "Education background", fields: [
      { key: "highest_qualification", label: "Highest completed qualification", type: "select", options: QUALS, required: true },
      { key: "field_of_study", label: "Field of study", required: true, placeholder: "e.g. Business · Engineering" },
      { key: "graduation_year", label: "Graduation year", type: "number" },
      { key: "study_gap", label: "Study gap since last qualification", type: "select", options: ["No gap", "Under 1 year", "1–2 years", "2–5 years", "5+ years"] },
      { key: "english_test", label: "English test taken", type: "select", options: ["IELTS", "PTE Academic", "TOEFL iBT", "OET", "Duolingo", "Not taken yet"] },
      { key: "english_score", label: "English score (if taken)", placeholder: "e.g. IELTS 6.5" },
    ]},
    { title: "Study preferences & goals", fields: [
      { key: "preferred_course_area", label: "Preferred course area", required: true, placeholder: "e.g. IT · Nursing · Accounting" },
      { key: "preferred_level", label: "Preferred qualification level", type: "select", options: ["Certificate / Diploma", "Bachelor", "Master", "PhD", "Not sure"] },
      { key: "preferred_location", label: "Preferred city or state", placeholder: "e.g. Sydney · Adelaide" },
      { key: "regional_ok", label: "Open to regional locations?", type: "select", options: YN },
      { key: "intake", label: "Preferred intake", type: "select", options: ["Feb 2027", "Jul 2027", "Nov 2027", "2028 or later", "Flexible"] },
      { key: "budget", label: "Annual tuition budget (AUD)", type: "select", options: ["Under $20,000", "$20,000–$30,000", "$30,000–$45,000", "$45,000+", "Not sure"] },
      { key: "scholarship_interest", label: "Interested in scholarships?", type: "select", options: ["Yes", "No"] },
      { key: "with_family", label: "Travelling with family?", type: "select", options: ["Yes", "No"] },
      { key: "long_term_goal", label: "What is your long-term goal?", type: "select", full: true, required: true, options: [
        "Return home after completing my studies", "Gain international education and work experience",
        "Explore skilled migration pathways later", "Seek employer sponsorship in the future",
        "Join family in Australia or New Zealand", "Build a long-term career in Australia or New Zealand", "I am unsure"] },
    ]},
  ],
  work: [
    { title: "Your work profile", fields: [
      { key: "occupation", label: "Current occupation", required: true },
      { key: "qualification", label: "Highest qualification", type: "select", options: QUALS, required: true },
      { key: "years_experience", label: "Total years of work experience", type: "number", required: true },
      { key: "australian_experience", label: "Any Australian or New Zealand work experience?", type: "select", options: ["Yes", "No"] },
      { key: "registration_required", label: "Occupation requires registration / licensing?", type: "select", options: YN },
      { key: "has_sponsor", label: "Employer in Australia or NZ willing to sponsor?", type: "select", options: ["Yes", "No", "In discussion"] },
      { key: "english_level", label: "English level", type: "select", options: ENGLISH },
      { key: "current_visa", label: "Current visa (if any)" },
    ]},
  ],
  pr: [
    { title: "Your PR profile", fields: [
      { key: "age", label: "Age", type: "number", required: true },
      { key: "occupation", label: "Occupation", required: true },
      { key: "skills_assessment", label: "Skills assessment status", type: "select", options: ["Positive result in hand", "In progress", "Not started"] },
      { key: "english_level", label: "English level", type: "select", options: ENGLISH },
      { key: "qualification", label: "Highest qualification", type: "select", options: QUALS },
      { key: "years_experience", label: "Years of skilled experience", type: "number" },
      { key: "australian_study", label: "Studied in Australia or New Zealand?", type: "select", options: ["Yes", "No"] },
      { key: "partner_status", label: "Partner situation", type: "select", options: ["Single", "Partner with skills/English", "Partner is Australian citizen/PR", "Partner without skills evidence"] },
      { key: "state_preference", label: "Preferred state / territory", placeholder: "e.g. NSW · SA · Any" },
    ]},
  ],
  family: [
    { title: "Your relationship & family situation", fields: [
      { key: "relationship_type", label: "Relationship type", type: "select", options: ["Married", "De facto", "Engaged", "Other family member"], required: true },
      { key: "sponsor_status", label: "Your partner / family member is", type: "select", options: ["Australian citizen", "Australian permanent resident", "New Zealand citizen / resident", "Other"], required: true },
      { key: "relationship_duration", label: "How long have you been together?", placeholder: "e.g. 2 years" },
      { key: "living_together", label: "Currently living together?", type: "select", options: ["Yes", "No", "Sometimes"] },
      { key: "children", label: "Children from the relationship?", type: "select", options: ["Yes", "No"] },
      { key: "previous_applications", label: "Previous visa applications / refusals?", type: "select", options: ["Yes", "No"] },
      { key: "current_visa", label: "Your current visa (if any)" },
    ]},
  ],
  visitor: [
    { title: "Your visit details", fields: [
      { key: "visit_reason", label: "Reason for visit", type: "select", options: ["Tourism", "Visiting family", "Visiting partner", "Business", "Medical", "Other"], required: true },
      { key: "duration", label: "Proposed duration", type: "select", options: ["Up to 3 months", "3–6 months", "6–12 months"], required: true },
      { key: "employment_status", label: "Employment in home country", placeholder: "e.g. Employed · Self-employed · Retired" },
      { key: "funds", label: "Funds available for the trip", type: "select", options: ["Self-funded", "Family in Australia / NZ supporting", "Both", "Not sure"] },
      { key: "travel_history", label: "Previous international travel?", type: "select", options: ["Extensive", "Some", "First trip"] },
      { key: "family_in_au", label: "Family ties in Australia or New Zealand?", type: "select", options: ["Yes", "No"] },
      { key: "previous_refusals", label: "Any previous visa refusals?", type: "select", options: ["Yes", "No"] },
    ]},
  ],
  sponsor: [
    { title: "Who are you sponsoring?", fields: [
      { key: "relationship", label: "Relationship to the applicant", type: "select", options: ["Parent", "Partner", "Child", "Other relative"], required: true },
      { key: "applicant_location", label: "Where is your family member now?", placeholder: "e.g. India · In Australia", required: true },
      { key: "sponsor_status", label: "Your status in Australia / New Zealand", type: "select", options: ["Australian citizen", "Australian permanent resident", "New Zealand citizen / resident", "Other"], required: true },
      { key: "family_composition", label: "Family composition", placeholder: "e.g. 2 parents, 3 children (2 in Australia)", full: true },
      { key: "support", label: "Support you can provide", type: "select", options: ["Financial", "Accommodation", "Both", "Not sure"] },
    ]},
  ],
  onshore: [
    { title: "Your current situation in Australia or New Zealand", fields: [
      { key: "current_visa", label: "Current visa", required: true, placeholder: "e.g. Student 500 · 485 · Bridging" },
      { key: "visa_expiry", label: "Visa expiry date", type: "date" },
      { key: "current_activity", label: "Currently studying / working as", placeholder: "e.g. Master of IT · Chef" },
      { key: "visa_history", label: "Previous Australian visas held", placeholder: "e.g. Student → 485" },
      { key: "refusals", label: "Any refusals or cancellations?", type: "select", options: ["Yes", "No"] },
      { key: "preferred_next_step", label: "Preferred next step", type: "select", options: ["Further study", "Graduate visa (485)", "Skilled migration / PR", "Employer sponsorship", "Partner visa", "Not sure"], required: true },
    ]},
  ],
  unsure: [
    { title: "Tell us a little about yourself", fields: [
      { key: "age_range", label: "Age range", type: "select", options: ["Under 18", "18–25", "26–32", "33–39", "40–49", "50+"], required: true },
      { key: "main_interest", label: "What interests you most?", type: "select", options: ["Studying", "Working", "Permanent residency", "Joining family", "Visiting", "Not sure"], required: true },
      { key: "current_location", label: "Where are you now?", placeholder: "e.g. Nepal · In Australia", required: true },
      { key: "timeline", label: "When would you like to move?", type: "select", options: ["As soon as possible", "Within 6 months", "Within 1–2 years", "Just exploring"] },
      { key: "situation", label: "Briefly describe your situation and goals", type: "textarea", placeholder: "e.g. current study/work, family, budget, any visa history…" },
    ]},
  ],
};

const CONTACT_STEP = {
  title: "Your contact details",
  fields: [
    { key: "destination_country", label: "Which country are you interested in?", type: "select", options: ["Australia", "New Zealand", "Both / Not sure"], required: true },
    { key: "full_name", label: "Full name", required: true },
    { key: "email", label: "Email", type: "email", required: true },
    { key: "phone", label: "Mobile number", required: true },
    { key: "whatsapp", label: "WhatsApp (if different)" },
    { key: "country", label: "Current country", required: true },
    { key: "preferred_contact", label: "Preferred contact method", type: "select", options: ["Email", "Phone", "WhatsApp"], required: true },
  ],
};

export default function PathwayAssessment() {
  const [goal, setGoal] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [values, setValues] = useState({});
  const [consent, setConsent] = useState(false);
  const [updatesConsent, setUpdatesConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const steps = goal ? [...BRANCH_STEPS[goal], CONTACT_STEP] : [];
  const isLast = goal && stepIdx === steps.length - 1;
  const onChange = (k, v) => setValues((s) => ({ ...s, [k]: v }));
  const top = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const next = async () => {
    const missing = validateStep(steps[stepIdx].fields, values);
    if (missing) { toast.error(`Please complete: ${missing}`); return; }
    if (!isLast) { setStepIdx(stepIdx + 1); top(); return; }
    if (!consent) { toast.error("Please tick the consent checkbox to submit."); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/pathway/submit`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ goal, ...values, updates_consent: updatesConsent, consent: true, consent_timestamp: new Date().toISOString() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Submission failed");
      setResult(data);
      toast.success("Assessment received!");
      top();
    } catch (err) { toast.error(err.message || "Something went wrong — please try again."); }
    finally { setLoading(false); }
  };

  const back = () => {
    if (stepIdx === 0) { setGoal(null); setValues({}); }
    else setStepIdx(stepIdx - 1);
    top();
  };

  if (result) {
    return (
      <div data-testid="pathway-result" className="bg-white">
        <PortalHero icon={CheckCircle2} eyebrow="Assessment received" title={`Thank you, ${(values.full_name || "").split(" ")[0]}!`} subtitle="Your assessment has been submitted and our team has been notified." />
        <section className="py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-[#F8F4FB] border-l-4 border-[#29324A] p-6">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#29324A]">Your Reference Number</div>
              <div className="font-display text-3xl text-[#29324A] mt-1" data-testid="pathway-reference">{result.reference_id}</div>
              <div className="text-[12px] text-slate-500 mt-1">Pathway: {result.pathway_label}</div>
            </div>
            <div className="mt-6 bg-white border border-slate-200 p-6">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#C43A89]">Recommended next step</div>
              <p className="text-[14px] text-slate-700 leading-relaxed mt-2">{result.recommended_next_step}</p>
              <p className="text-[13px] text-slate-600 mt-3">A confirmation email has been sent to <strong>{values.email}</strong>. Expect a response within 1 business day.</p>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="pathway-shortlist-cta" className="inline-flex items-center gap-2 bg-[#29324A] hover:bg-[#472e70] text-white text-xs font-bold tracking-[0.2em] uppercase px-6 py-4">
                Request My Shortlist <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/book-consultation" data-testid="pathway-book-cta" className="inline-flex items-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-[0.2em] uppercase px-6 py-4">
                Book a Migration Consultation
              </Link>
            </div>
            <ComplianceNote />
          </div>
        </section>
        <Disclaimer />
      </div>
    );
  }

  return (
    <div data-testid="pathway-assessment-page" className="bg-white">
      <PortalHero
        icon={Sparkles}
        eyebrow="Smart Pathway Assessment · Free"
        title="What would you like to achieve in Australia or New Zealand?"
        subtitle="Answer a few tailored questions and receive a personalised recommendation for your study or migration journey — reviewed by our team within 1 business day."
      />

      <section className="py-12 md:py-16 bg-[#F8F9FA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {!goal ? (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="goal-grid">
              {GOALS.map(({ key, icon: Icon, label, desc }) => (
                <button key={key} type="button" data-testid={`goal-${key}`} onClick={() => { setGoal(key); setStepIdx(0); top(); }}
                  className="group text-left bg-white border border-slate-200 hover:border-[#C43A89] hover:shadow-md transition-all p-5 flex gap-4">
                  <span className="shrink-0 h-11 w-11 bg-[#F8F4FB] border border-[#29324A]/20 flex items-center justify-center group-hover:bg-[#29324A] transition-colors">
                    <Icon className="h-5 w-5 text-[#29324A] group-hover:text-white transition-colors" />
                  </span>
                  <span>
                    <span className="font-display text-[16px] text-[#29324A] leading-tight block">{label}</span>
                    <span className="text-[12px] text-slate-500 mt-1 block">{desc}</span>
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="bg-white border border-slate-200 shadow-sm border-l-4 border-l-[#29324A] p-6 md:p-8">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#C43A89] mb-1">
                {GOALS.find((g) => g.key === goal)?.label}
              </div>
              <WizardProgress titles={steps.map((s) => s.title)} current={stepIdx} />

              {stepIdx === 0 && TOOLS[goal] && (
                <div className="mb-6 bg-[#F8F4FB] border border-[#29324A]/20 p-4 flex items-start gap-3" data-testid="tool-suggestion">
                  <Wrench className="h-4 w-4 text-[#29324A] shrink-0 mt-0.5" />
                  <div className="text-[12.5px] text-slate-700 leading-relaxed">
                    <strong className="text-[#29324A]">Want a deeper dive?</strong> We also have dedicated tools for this:{" "}
                    {TOOLS[goal].map((t, i) => (
                      <span key={t.to}>
                        {i > 0 && " · "}
                        <Link to={t.to} className="text-[#C43A89] underline font-semibold">{t.label}</Link>
                      </span>
                    ))}
                    . Or continue below for a quick guided assessment.
                  </div>
                </div>
              )}

              <FieldGrid fields={steps[stepIdx].fields} values={values} onChange={onChange} />

              {isLast && (
                <div className="mt-6">
                  <ConsentCheckbox value={consent} onChange={setConsent} testid="pathway-consent" />
                  <label className="flex items-start gap-3 text-xs text-slate-600 leading-relaxed cursor-pointer mt-2 pl-0">
                    <input type="checkbox" checked={updatesConsent} onChange={(e) => setUpdatesConsent(e.target.checked)} data-testid="pathway-updates-consent" className="mt-0.5 h-4 w-4 accent-[#29324A] shrink-0" />
                    <span>I&apos;d like to receive relevant migration and study updates from us (optional).</span>
                  </label>
                </div>
              )}

              <WizardNav onBack={back} onNext={next} nextLabel={isLast ? "Get My Recommendation" : "Continue"} loading={loading} />
            </div>
          )}
          {!goal && <ComplianceNote />}
        </div>
      </section>
      <Disclaimer />
    </div>
  );
}
